import { useEffect, useState } from "react";
import { setPage, toPrettyDate, useHub } from "../../../../core/Global";
import { toastSuccess } from "../../../../components/base/Toast";
import { FontAwesomeIcons } from "../../../../core/Types";
import Icon from "../../../../components/base/Icon";
import FlexRow from "../../../../components/base/FlexRow";
import Select from "../../../../components/form/Select";
import TextBox from "../../../../components/form/TextBox";
import {
  PercallDuplicateActions, PercallKinds, PercallKindSettings, PercallMappingItem, PercallMappingTarget, PercallNewNameActions,
  PercallOptions, PercallOrigins, PercallSettingsData
} from "../models/PercallSettings";


type ListFilter = "all" | "unmapped" | "auto" | "manual"

const ORIGINS: Record<PercallOrigins, { text: string, icon: FontAwesomeIcons, badge: string, hint: string }> = {
  [PercallOrigins.None]: { text: "Not Mapped", icon: "link-slash", badge: "badge-light-danger", hint: "Its jobs come in without one." },
  [PercallOrigins.NameMatch]: { text: "Same Name", icon: "equals", badge: "badge-light-success", hint: "Matched with the one of the same name." },
  [PercallOrigins.DefaultTarget]: { text: "Rule", icon: "wand-magic-sparkles", badge: "badge-light-info", hint: "Sent here by the rule for new names." },
  [PercallOrigins.Created]: { text: "Created", icon: "plus", badge: "badge-light-primary", hint: "Created in Rinvox when Percall first sent it." },
  [PercallOrigins.Manual]: { text: "Manual", icon: "hand-pointer", badge: "badge-light-warning", hint: "Mapped by hand." },
}

interface OptionItem<T> {
  value: T
  title: string
  icon: FontAwesomeIcons
  description: string
  isDefault?: boolean
}

const DUPLICATE_OPTIONS: OptionItem<PercallDuplicateActions>[] = [
  {
    value: PercallDuplicateActions.Accept, title: "Accept", icon: "check",
    description: "Save the job as it is, however often it repeats."
  },
  {
    value: PercallDuplicateActions.AcceptAndFlag, title: "Accept and flag", icon: "flag", isDefault: true,
    description: "Save the job and mark it with a \"Per Call Duplicated\" badge."
  },
  {
    value: PercallDuplicateActions.Refuse, title: "Refuse", icon: "ban",
    description: "Do not create the job at all."
  },
]

const ruleOptions = (noun: string): OptionItem<PercallNewNameActions>[] => [
  {
    value: PercallNewNameActions.LeaveEmpty, title: "Leave empty", icon: "link-slash", isDefault: true,
    description: `The job comes in without a ${noun}; a note tells dispatch what Percall sent.`
  },
  {
    value: PercallNewNameActions.CreateNew, title: "Create it", icon: "plus",
    description: `Create a ${noun} of that name in Rinvox.`
  },
  {
    value: PercallNewNameActions.MapToDefault, title: "Map to", icon: "arrow-right",
    description: `Send it to a ${noun} you choose.`
  },
]

const matchesFilter = (m: PercallMappingItem, filter: ListFilter) =>
  filter === "all"
  || (filter === "unmapped" && !m.targetId)
  || (filter === "auto" && !!m.targetId && m.origin !== PercallOrigins.Manual)
  || (filter === "manual" && m.origin === PercallOrigins.Manual);


/** A job type as "Group ↳ Type" -- the group is what tells two types of the same name apart. */
const TargetText = (p: { item?: PercallMappingTarget }) => (
  <span className="d-inline-flex align-items-center text-truncate">
    {
      p.item?.groupName &&
      <>
        <span className="text-muted">{p.item.groupName}</span>
        <Icon icon="arrow-turn-down-right" size={10} className="mx-2 text-gray-400" />
      </>
    }
    <span>{p.item?.name}</span>
  </span>
)

const TargetSelect = (p: {
  id: string, targets: PercallMappingTarget[], value?: string, placeholder: string, disabled?: boolean, deselect?: boolean,
  onSelect: (val?: string) => void
}) => (
  <Select id={p.id} small block marginBottom={0}
    source={p.targets} dataText="name" dataValue="id"
    searchText={i => `${i.groupName ?? ""} ${i.name ?? ""}`}
    optionTemplate={i => <TargetText item={i} />}
    selectionTemplate={i => <TargetText item={i} />}
    placeholder={p.placeholder} disabled={p.disabled} deselect={p.deselect}
    defaultValue={p.value} value={p.value}
    onSelect={p.onSelect} />
)



/** A small segmented control -- the settings' radio list. The chosen option's description shows under it. */
function Segmented<T extends number>(p: { id: string, options: OptionItem<T>[], value?: T, disabled?: boolean, onChoose: (val: T) => void }) {
  const current = p.options.find(o => o.value === p.value);
  return (
    <div>
      <div className="d-inline-flex flex-wrap gap-1 p-1 rounded bg-light border border-gray-300" role="group">
        {
          p.options.map(o => {
            const active = p.value === o.value;
            return (
              <button key={o.value} id={`${p.id}-${o.value}`} type="button" disabled={p.disabled}
                title={o.description}
                className={`btn btn-sm px-3 py-1 fs-8 fw-semibold ${active ? "btn-primary" : "btn-color-gray-700 btn-active-color-primary bg-transparent"}`}
                onClick={() => { if (!active) p.onChoose(o.value) }}>
                <Icon icon={o.icon} size={10} className="me-1" />
                {o.title}
              </button>
            )
          })
        }
      </div>
      {
        current &&
        <div className="fs-8 text-muted mt-1">
          {current.description}
          {current.isDefault && <span className="text-gray-500"> (default)</span>}
        </div>
      }
    </div>
  )
}

/** One setting: its name and a one-line hint on the left, the control on the right. */
const SettingRow = (p: { title: string, hint?: any, children: any }) => (
  <div className="d-flex flex-wrap align-items-start gap-3 py-3">
    <div style={{ width: 220, minWidth: 180 }}>
      <div className="fw-semibold fs-7 text-gray-800">{p.title}</div>
      {p.hint && <div className="fs-8 text-muted">{p.hint}</div>}
    </div>
    <div className="flex-grow-1" style={{ minWidth: 260 }}>
      {p.children}
    </div>
  </div>
)

/** A titled group of settings inside the Settings card. */
const SettingGroup = (p: { title: string, hint: any, saving?: boolean, last?: boolean, children: any }) => (
  <div className={`py-5 ${p.last ? "" : "border-bottom border-gray-200"}`}>
    <FlexRow className="mb-1">
      <span className="fw-bold fs-6 text-gray-900">{p.title}</span>
      {p.saving && <Icon icon="spinner-third" spin size={12} className="text-primary ms-2" />}
    </FlexRow>
    <div className="fs-8 text-muted mb-2">{p.hint}</div>
    {p.children}
  </div>
)


/**
 * The Duplicate job behaviour setting, chosen apart for a repeat of an open job from the same job
 * source and from a different one.
 */
function DuplicateSetting(p: { options: PercallOptions, onChange: (o: PercallOptions) => void }) {

  const hub = useHub();
  const [saving, savingSet] = useState<boolean>(false);

  const save = (field: "duplicateAction" | "otherSourceDuplicateAction", value: PercallDuplicateActions) => {
    if (saving || value === (p.options[field] ?? PercallDuplicateActions.AcceptAndFlag)) return;
    savingSet(true);
    hub.Post("/base/PercallSettings/options", { [field]: value }, res => {
      savingSet(false);
      if (res.success) {
        p.onChange(res.data);
        toastSuccess("Duplicate job behaviour has been saved.");
      }
    })
  }

  return (
    <SettingGroup title="Duplicate jobs" saving={saving}
      hint={<>
        Same phone number as an <b>open</b> job (not closed, canceled or archived), whatever its name, address
        or job type.
      </>}>
      <SettingRow title="Same job source" hint="Percall sent the same lead again.">
        <Segmented id="opt-percall-duplicate" options={DUPLICATE_OPTIONS} disabled={saving}
          value={p.options.duplicateAction ?? PercallDuplicateActions.AcceptAndFlag}
          onChoose={v => save("duplicateAction", v)} />
      </SettingRow>
      <SettingRow title="Different job source" hint="Came in again through another source.">
        <Segmented id="opt-percall-duplicate-other" options={DUPLICATE_OPTIONS} disabled={saving}
          value={p.options.otherSourceDuplicateAction ?? PercallDuplicateActions.AcceptAndFlag}
          onChoose={v => save("otherSourceDuplicateAction", v)} />
      </SettingRow>
    </SettingGroup>
  )
}


/** What a job type or source name Percall sends for the first time becomes. */
function NewNameSetting(p: {
  kind: PercallKinds, settings: PercallKindSettings, groups: { id: string, name: string }[],
  onChange: (change: (s: PercallKindSettings) => PercallKindSettings) => void
}) {

  const hub = useHub();

  // "Map to" needs a target before it can be saved, so picking it only shows the select until one is chosen.
  const [draftAction, draftActionSet] = useState<PercallNewNameActions>();
  const [saving, savingSet] = useState<boolean>(false);

  const { kind, settings } = p;
  const isType = kind === PercallKinds.JobType;
  const noun = isType ? "job type" : "job source";
  const rule = settings.rule;
  const action = draftAction ?? rule.action ?? PercallNewNameActions.LeaveEmpty;

  const saveRule = (next: PercallNewNameActions, targetId?: string, groupId?: string) => {
    savingSet(true);
    hub.Post("/base/PercallSettings/rule", { kind, action: next, targetId, groupId }, res => {
      savingSet(false);
      if (res.success) {
        draftActionSet(undefined);
        p.onChange(s => ({ ...s, rule: res.data }));
        toastSuccess(`The rule for new ${noun}s has been saved.`);
      }
    })
  }

  const choose = (next: PercallNewNameActions) => {
    if (saving || next === action) return;
    if (next === PercallNewNameActions.MapToDefault) {
      draftActionSet(next);
      return;
    }
    draftActionSet(undefined);
    saveRule(next);
  }

  return (
    <SettingRow title={isType ? "Job types" : "Job sources"}>
      <div className="d-flex flex-wrap align-items-start gap-3">
        <Segmented id={`opt-percall-rule-${kind}`} options={ruleOptions(noun)} disabled={saving}
          value={action} onChoose={choose} />

        {
          action === PercallNewNameActions.CreateNew && isType &&
          <div style={{ width: 220, maxWidth: "100%" }}>
            <Select id="sel-percall-rule-group" small block marginBottom={0}
              source={p.groups} dataText="name" dataValue="id"
              placeholder="In no group" disabled={saving}
              defaultValue={rule.groupId} value={rule.groupId}
              onSelect={val => saveRule(PercallNewNameActions.CreateNew, undefined, val)} />
          </div>
        }

        {
          action === PercallNewNameActions.MapToDefault &&
          <div style={{ width: 260, maxWidth: "100%" }}>
            <TargetSelect id={`sel-percall-rule-target-${kind}`} targets={settings.targets}
              placeholder={`Choose a ${noun}`} deselect={false} disabled={saving}
              value={rule.action === PercallNewNameActions.MapToDefault ? rule.targetId : undefined}
              onSelect={val => { if (val) saveRule(PercallNewNameActions.MapToDefault, val) }} />
            {
              draftAction === PercallNewNameActions.MapToDefault &&
              <div className="fs-8 text-warning mt-1">Choose a {noun} to save this rule.</div>
            }
          </div>
        }

        {saving && <Icon icon="spinner-third" spin size={12} className="text-primary mt-2" />}
      </div>
    </SettingRow>
  )
}




interface MappingListProps {
  kind: PercallKinds
  settings: PercallKindSettings
  keyword: string
  filter: ListFilter
  onChange: (change: (s: PercallKindSettings) => PercallKindSettings) => void
}

/** Every job type or job source name received from Percall and where it goes. */
function MappingList(props: MappingListProps) {

  const hub = useHub();
  const [savingId, savingIdSet] = useState<string>();

  const { kind, settings } = props;
  const isType = kind === PercallKinds.JobType;
  const noun = isType ? "job type" : "job source";
  const Nouns = isType ? "Job Types" : "Job Sources";

  const replaceRow = (row: PercallMappingItem) => {
    props.onChange(s => ({ ...s, mappings: s.mappings.map(m => m.id === row.id ? row : m) }));
  }

  const mapRow = (row: PercallMappingItem, targetId?: string) => {
    if ((targetId ?? "") === (row.targetId ?? "") && row.origin === PercallOrigins.Manual) return;
    savingIdSet(row.id);
    hub.Post("/base/PercallSettings/map", { kind, id: row.id, targetId }, res => {
      savingIdSet(undefined);
      if (res.success) {
        replaceRow(res.data);
        toastSuccess(`"${row.name}" has been mapped.`);
      }
    })
  }

  const backToAutomatic = (row: PercallMappingItem) => {
    savingIdSet(row.id);
    hub.Post("/base/PercallSettings/automatic", { kind, id: row.id }, res => {
      savingIdSet(undefined);
      if (res.success) {
        replaceRow(res.data);
        toastSuccess(`"${row.name}" is mapped automatically again.`);
      }
    })
  }

  const mappings = settings.mappings;
  const unmapped = mappings.filter(m => !m.targetId).length;
  const k = props.keyword.trim().toLowerCase();
  const visible = mappings
    .filter(m => !k || (m.name ?? "").toLowerCase().includes(k) || (m.targetName ?? "").toLowerCase().includes(k)
      || (m.targetGroupName ?? "").toLowerCase().includes(k))
    .filter(m => matchesFilter(m, props.filter));

  return (
    <div className="card h-100">
      <div className="card-header align-items-center min-h-50px px-7">
        <div className="card-title m-0">
          <Icon icon={isType ? "list" : "share-nodes"} size={14} className="me-2 text-primary" />
          <span className="fw-bold fs-5 text-gray-900">{Nouns}</span>
          <span className="badge badge-light ms-3">{mappings.length}</span>
          {unmapped > 0 && <span className="badge badge-light-danger ms-2">{unmapped} not mapped</span>}
        </div>
      </div>

      <div className="card-body px-7 py-1">
        {
          mappings.length === 0 ?
            <div className="text-center py-10">
              <Icon icon="inbox" size={24} className="text-gray-300 mb-2" />
              <div className="fs-7 fw-bold text-gray-700">Nothing received yet</div>
              <div className="fs-7 text-muted mt-1">{Nouns} appear here once Percall sends jobs.</div>
            </div>
            : visible.length === 0 ?
              <div className="text-center text-muted fs-7 py-10">No {noun}s match.</div>
              :
              visible.map(row => {
                // Rows saved before the origin was recorded have a target but no origin: those were name matches.
                const origin = ORIGINS[!row.targetId && row.origin !== PercallOrigins.Manual ? PercallOrigins.None
                  : (row.origin ?? PercallOrigins.None) === PercallOrigins.None ? PercallOrigins.NameMatch : row.origin!];
                const saving = savingId === row.id;
                return (
                  <div key={row.id} className="d-flex flex-wrap align-items-center gap-3 py-2 border-bottom border-gray-200 border-bottom-dashed">
                    <div className="flex-grow-1" style={{ minWidth: 160, flexBasis: 160 }}>
                      <div className="fw-semibold fs-7 text-gray-900">{row.name}</div>
                      <div className="fs-8 text-muted">
                        {row.jobsCount ?? 0} {row.jobsCount === 1 ? "job" : "jobs"} · {toPrettyDate(row.lastSeenAt)}
                      </div>
                    </div>

                    <div style={{ width: 260, maxWidth: "100%" }}>
                      <TargetSelect id={`sel-percall-${row.id}`} targets={settings.targets}
                        placeholder={`No ${noun}`} disabled={saving} value={row.targetId}
                        onSelect={val => mapRow(row, val)} />
                    </div>

                    <FlexRow style={{ width: 120 }}>
                      {
                        saving ?
                          <Icon icon="spinner-third" spin size={12} className="text-primary" />
                          :
                          <span className={`badge ${origin.badge}`}
                            title={origin.hint + (row.origin === PercallOrigins.Manual && row.mappedByUser ? ` By ${row.mappedByUser.fullName}.` : "")}>
                            <Icon icon={origin.icon} size={10} className="me-1" />
                            {origin.text}
                          </span>
                      }
                      {
                        row.origin === PercallOrigins.Manual && !saving &&
                        <a href="#" className="ms-2 text-gray-500 text-hover-primary" title="Back to automatic mapping"
                          onClick={e => { e.preventDefault(); backToAutomatic(row); }}>
                          <Icon icon="rotate-left" size={12} />
                        </a>
                      }
                    </FlexRow>
                  </div>
                )
              })
        }
      </div>
    </div>
  )
}


/**
 * CRM > Settings > Percall Settings.
 *
 * The main settings come first: what a job Percall sends again becomes (Duplicate job behaviour)
 * and what a job type or source name it sends for the first time becomes. Below them every name
 * received so far and where it goes -- job types and job sources side by side.
 *
 * The names only ever come from Percall, so nothing is added or deleted here. Picking a target on
 * a row maps it by hand, which the webhook then follows even when one of the same name exists.
 * Every choice saves at once.
 */
export default function PercallSettings() {

  const hub = useHub();

  const [data, dataSet] = useState<PercallSettingsData>();
  const [keyword, keywordSet] = useState<string>("");
  const [filter, filterSet] = useState<ListFilter>("all");

  useEffect(() => {
    setPage("Percall Settings", "CRM", "CRM Settings");
    hub.Get("/base/PercallSettings/", res => {
      if (res.success) dataSet(res.data);
    })
  }, [])

  const updateKind = (k: "jobTypes" | "jobSources") => (change: (s: PercallKindSettings) => PercallKindSettings) => {
    dataSet(d => d ? { ...d, [k]: change(d[k]) } : d);
  }

  const all = [...(data?.jobTypes.mappings ?? []), ...(data?.jobSources.mappings ?? [])];
  const count = (f: ListFilter) => all.filter(m => matchesFilter(m, f)).length;

  const FilterPill = (p: { value: ListFilter, title: string }) => (
    <button type="button"
      className={`btn btn-sm px-3 py-1 fs-8 ${filter === p.value ? "btn-primary" : "btn-light"}`}
      onClick={() => filterSet(p.value)}>
      {p.title}
      <span className={`badge ms-2 ${filter === p.value ? "badge-white text-primary" : "badge-secondary"}`}>{count(p.value)}</span>
    </button>
  )

  if (!data) {
    return (
      <div className="card m-5 m-lg-8">
        <div className="card-body py-20 text-center text-muted">
          <Icon icon="spinner-third" spin size={24} />
        </div>
      </div>
    )
  }

  return (
    <div className="px-5 px-lg-8 py-5">
      <div className="card mb-6">
        <div className="card-header align-items-center min-h-50px px-7">
          <div className="card-title m-0">
            <Icon icon="gear" size={14} className="me-2 text-primary" />
            <span className="fw-bold fs-5 text-gray-900">Settings</span>
          </div>
          <div className="card-toolbar fs-8 text-muted">Changes save at once and apply to new jobs only.</div>
        </div>
        <div className="card-body px-7 py-0">
          <DuplicateSetting options={data.options ?? {}} onChange={o => dataSet(d => d ? { ...d, options: o } : d)} />
          <SettingGroup title="New names" last
            hint="When Percall sends a job type or source the account has nothing of the same name for.">
            <NewNameSetting kind={PercallKinds.JobType} settings={data.jobTypes} groups={data.jobTypeGroups}
              onChange={updateKind("jobTypes")} />
            <NewNameSetting kind={PercallKinds.JobSource} settings={data.jobSources} groups={data.jobTypeGroups}
              onChange={updateKind("jobSources")} />
          </SettingGroup>
        </div>
      </div>

      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div>
          <div className="fw-bold fs-5 text-gray-900">Mappings</div>
          <div className="fs-8 text-muted">Where each name Percall has sent goes. Jobs already received are not changed.</div>
        </div>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <FilterPill value="all" title="All" />
          <FilterPill value="unmapped" title="Not Mapped" />
          <FilterPill value="auto" title="Automatic" />
          <FilterPill value="manual" title="Manual" />
          <div className="ms-md-3" style={{ width: 260, minWidth: 200, maxWidth: "100%" }}>
            <TextBox id="txt-percall-search" small block icon="magnifying-glass" noMarginBottom
              placeholder="Search names..." onTextChanges={t => keywordSet(t)} />
          </div>
        </div>
      </div>

      <div className="row g-5">
        <div className="col-xl-6">
          <MappingList kind={PercallKinds.JobType} settings={data.jobTypes}
            keyword={keyword} filter={filter} onChange={updateKind("jobTypes")} />
        </div>
        <div className="col-xl-6">
          <MappingList kind={PercallKinds.JobSource} settings={data.jobSources}
            keyword={keyword} filter={filter} onChange={updateKind("jobSources")} />
        </div>
      </div>
    </div>
  );
}
